import {useTestRoom} from "../../../../../hooks/useTestRoom";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Divider, Stack, Typography} from "@mui/joy";
import {Question, QuestionGroup, QuestionType, RawQuestion} from "../../../../../models/question";
import IeltsMatchingQuestionComponent from "../question/IeltsMatchingQuestionComponent";
import IeltsMultipleChoiceQuestionComponent from "../question/IeltsMultipleChoiceQuestionComponent";
import IeltsOpenAnswerQuestionComponent from "../question/IeltsOpenAnswerQuestionComponent";
import IeltsTrueFalseQuestionComponent from "../question/IeltsTrueFalseQuestionComponent";

const IeltsListeningTestRoomComponent = (props: { audioInstructionsUrl: string }) => {
    const testRoom = useTestRoom()
    const [questionSet, setQuestionSet] = useState<QuestionGroup>()
    const audioPlayerRef = useRef<HTMLAudioElement>(null)
    const instructionPlayerRef = useRef<HTMLAudioElement>(null)

    // refs to avoid stale closures in callbacks
    const currentSubsectionNumberRef = useRef(testRoom.currentSubsectionNumber)
    const questionSetRef = useRef(questionSet)

    useEffect(() => {
        currentSubsectionNumberRef.current = testRoom.currentSubsectionNumber
    }, [testRoom.currentSubsectionNumber])

    useEffect(() => {
        questionSetRef.current = questionSet
    }, [questionSet])

    const handlePlaybackEnd = () => {
        if (!testRoom.reachedSubsectionsRef.current.includes(currentSubsectionNumberRef.current))
            testRoom.awaitTimer(60, async () => {
                audioPlayerRef.current = new Audio(questionSetRef.current?.metadata['audioUrl'])
                audioPlayerRef.current.onloadedmetadata = () =>
                    testRoom.reachedSubsectionsRef.current.push(currentSubsectionNumberRef.current)
                await audioPlayerRef.current.play()
            })
    }

    const handlePlayInstruction = async () => {
        if (!audioPlayerRef.current?.paused)
            audioPlayerRef.current?.pause()

        if (!testRoom.reachedSubsectionsRef.current.includes(currentSubsectionNumberRef.current))
            instructionPlayerRef.current?.play().catch(console.error)
    }

    const releasePlayer = () => {
        if (!instructionPlayerRef.current?.paused)
            instructionPlayerRef.current?.pause()

        if (!audioPlayerRef.current?.paused)
            audioPlayerRef.current?.pause()

        if (audioPlayerRef.current)
            audioPlayerRef.current.currentTime = 0

        if (instructionPlayerRef.current)
            instructionPlayerRef.current.currentTime = 0
    }

    // drive src change imperatively when subsection changes
    useEffect(() => {
        if (!instructionPlayerRef.current || !props.audioInstructionsUrl) return

        instructionPlayerRef.current.src = props.audioInstructionsUrl
        instructionPlayerRef.current.load()
    }, [props.audioInstructionsUrl])

    useEffect(() => {
        if (testRoom.currentSubsectionNumber)
            handlePlayInstruction()

        return releasePlayer
    }, [testRoom.currentSubsectionNumber])

    useEffect(() => {
        setQuestionSet(testRoom.questions?.find(
            value => value.subSectionNumber === testRoom.currentSubsectionNumber
        ) as QuestionGroup)
    }, [testRoom.currentSubsectionNumber, testRoom.questions])

    const QuestionComponent = (props: { question: RawQuestion, questionGroupIndex: number }) => {
        switch (props.question.type) {
            case "matching":
                return <IeltsMatchingQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                       question={props.question} questionId={questionSet?.id}
                                                       testId={questionSet?.testId}
                                                       sectionName={questionSet?.sectionName}
                                                       subSectionNumber={questionSet?.subSectionNumber}/>
            case "multiple_choice":
                return <IeltsMultipleChoiceQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                             question={props.question} parentQuestion={questionSet}/>
            case "fill_blank":
            case "short_answer":
                return <IeltsOpenAnswerQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                         question={props.question} questionId={questionSet?.id}
                                                         testId={questionSet?.testId}
                                                         sectionName={questionSet?.sectionName}
                                                         subSectionNumber={questionSet?.subSectionNumber}/>
            case "true_false_not_given":
                return <IeltsTrueFalseQuestionComponent questionGroupIndex={props.questionGroupIndex}
                                                        question={props.question} questionId={questionSet?.id}
                                                        testId={questionSet?.testId}
                                                        sectionName={questionSet?.sectionName}
                                                        subSectionNumber={questionSet?.subSectionNumber}/>
        }
    }

    return <Stack gap={4}>

        {/* always mounted at top level — never inside a conditional */}
        <audio
            style={{display: "none"}}
            ref={instructionPlayerRef}
            onLoadedMetadata={() => {
                if (instructionPlayerRef.current)
                    instructionPlayerRef.current.playbackRate = 0.9
            }}
            onEnded={handlePlaybackEnd}
        />

        {questionSet?.questions.map((question, index) =>
            <QuestionComponent questionGroupIndex={index} question={question}/>
        )}

    </Stack>
}
export default React.memo(IeltsListeningTestRoomComponent)